import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, ArrowUpCircle, Crown } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/apiClient';

const SubscriptionWidget = ({ onUpgradeClick }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await apiClient.get('/billing/subscription');
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return null;
  }

  const { plan_name, limits, status, trial } = subscription;
  const { products_with_audio, custom_voices_created } = usage || {};

  // Check if on active trial
  const isOnTrial = trial?.is_trial && trial?.trial_active;
  
  const productPercentage = limits.max_products === 999999 
    ? 0 
    : (products_with_audio / limits.max_products) * 100;
  
  const voicePercentage = limits.max_custom_voices === 0 
    ? 0 
    : (custom_voices_created / limits.max_custom_voices) * 100;

  const isNearLimit = productPercentage >= 80 || voicePercentage >= 80;
  const isAtLimit = productPercentage >= 100 || voicePercentage >= 100;

  const getPlanBadgeColor = () => {
    switch (plan_name) {
      case 'free':
        return 'bg-gray-500';
      case 'starter':
        return 'bg-blue-500';
      case 'professional':
        return 'bg-purple-500';
      case 'enterprise':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <Card className={isAtLimit ? 'border-red-500' : isNearLimit ? 'border-yellow-500' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {plan_name === 'enterprise' && <Crown className="w-5 h-5 text-amber-500" />}
            Subscription
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={getPlanBadgeColor()}>
              {plan_name?.toUpperCase()}
            </Badge>
            {isOnTrial && (
              <Badge className="bg-green-500">
                TRIAL
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Trial Info Banner */}
        {isOnTrial && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm font-semibold text-green-900">
              🎉 Free Trial Active - {trial.days_remaining} days remaining
            </p>
            <p className="text-xs text-green-700 mt-1">
              {trial.products_limit - trial.products_used} products left. Your trial custom voice becomes permanent when you upgrade!
            </p>
          </div>
        )}

        {/* Products Usage */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium">Products with Audio</span>
            <span className="text-sm text-gray-600">
              {products_with_audio} / {limits.max_products === 999999 ? '∞' : limits.max_products}
            </span>
          </div>
          {limits.max_products !== 999999 && (
            <Progress value={productPercentage} className="h-2" />
          )}
        </div>

        {/* Custom Voices Usage */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm font-medium">Custom Voices</span>
            <span className="text-sm text-gray-600">
              {custom_voices_created} / {limits.max_custom_voices || '0'}
            </span>
          </div>
          {limits.max_custom_voices > 0 && (
            <Progress value={voicePercentage} className="h-2" />
          )}
          {limits.max_custom_voices === 0 && !isOnTrial && (
            <p className="text-xs text-gray-500 mt-1">
              Not available on Free plan
            </p>
          )}
          {isOnTrial && limits.max_custom_voices === 0 && trial.custom_voice_used && (
            <p className="text-xs text-green-600 mt-1">
              ✓ Trial custom voice created
            </p>
          )}
        </div>

        {/* Warnings and Upgrade Prompts */}
        {isAtLimit && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Limit Reached</p>
              <p className="text-xs text-red-700 mt-1">
                You&apos;ve reached your plan limits. Upgrade to continue adding more.
              </p>
            </div>
          </div>
        )}

        {isNearLimit && !isAtLimit && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">Approaching Limit</p>
              <p className="text-xs text-yellow-700 mt-1">
                You&apos;re close to your plan limits. Consider upgrading soon.
              </p>
            </div>
          </div>
        )}

        {plan_name === 'free' && !isOnTrial && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-900 mb-2">
              Unlock custom voice cloning and more products with a paid plan!
            </p>
          </div>
        )}

        {/* Upgrade Button */}
        {plan_name !== 'enterprise' && (
          <Button
            onClick={onUpgradeClick}
            className="w-full"
            variant={isAtLimit ? 'destructive' : 'default'}
          >
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            {isAtLimit ? 'Upgrade Now' : 'View Plans'}
          </Button>
        )}

        {status !== 'active' && (
          <p className="text-xs text-gray-500 text-center">
            Status: {status}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionWidget;
